import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BarChart3, Users, Cpu, Layers, CreditCard, Sparkles, Percent, Globe, LogOut, Zap, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

const inp = "w-full rounded-lg border border-[#212230] bg-[#101118] px-3 py-2 text-[12px] outline-none focus:border-[#ff4b2b]/60";
const NAV = [
  ["", BarChart3, "Dashboard"], ["users", Users, "Users"], ["models", Cpu, "AI Models"],
  ["plans", Layers, "Plans & Packs"], ["templates", Sparkles, "Templates"],
  ["payments", CreditCard, "Payments"], ["pricing", Percent, "Credit pricing"],
  ["alerts", AlertTriangle, "Margin alerts"],
  ["seo", Globe, "SEO pages"],
];

export function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && (user === false || (user && user.role !== "admin"))) nav("/studio", { replace: true });
  }, [user, loading, nav]);

  if (loading || !user) return <div className="grid min-h-screen place-items-center"><p className="mono text-[11px] uppercase tracking-[0.24em] text-[#9194a6]">…</p></div>;

  return (
    <div className="min-h-screen bg-[#050508]" data-testid="admin-shell">
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-[#212230] bg-[#08080d]/95 px-6 backdrop-blur-xl">
        <p className="font-display text-[13px] font-semibold tracking-tight">
          ADMIN STUDIO <span className="text-[#ff4b2b]">MOVIES IA</span>
        </p>
        <div className="flex items-center gap-3">
          <Link to="/studio" className="btn-ghost rounded-full px-4 py-1.5 text-[11px] font-semibold">Studio</Link>
          <button onClick={async () => { await logout(); nav("/en"); }} data-testid="admin-logout" className="grid h-8 w-8 place-items-center rounded-full border border-[#2a2c3c]">
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>
      <aside className="fixed bottom-0 left-0 top-14 hidden w-[210px] border-r border-[#212230] bg-[#08080d] p-3 lg:block">
        {NAV.map(([path, Icon, label]) => (
          <NavLink key={path} to={`/admin/${path}`} end={path === ""} data-testid={`admin-nav-${path || "dashboard"}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] transition-colors ${isActive ? "bg-white/[0.07] text-white" : "text-[#9194a6] hover:text-white"}`}>
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </aside>
      <main className="pt-14 lg:pl-[210px]"><Outlet /></main>
    </div>
  );
}

export function AdminDashboard() {
  const [d, setD] = useState(null);
  const [prof, setProf] = useState([]);
  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {});
    api.get("/admin/profitability?group=model").then(({ data }) => setProf(data)).catch(() => {});
  }, []);
  if (!d) return <div className="p-10 text-[13px] text-[#9194a6]">…</div>;

  const cards = [
    ["Users", d.users], ["Active 30d", d.active_users_30d], ["Paying", d.paying_users],
    ["MRR", `€${d.mrr}`], ["ARR", `€${d.arr}`], ["ARPU", `€${d.arpu}`],
    ["LTV est.", `€${d.ltv_estimate}`], ["Conversion", `${d.conversion_rate}%`],
    ["Generations", d.generations], ["Failure rate", `${d.failure_rate}%`],
    ["Credits consumed", d.credits_consumed], ["Credits granted", d.credits_granted],
    ["AI cost", `€${d.ai_cost_eur}`], ["Credit revenue eq.", `€${d.credit_revenue_equivalent_eur}`],
    ["Gross margin", `€${d.gross_margin_eur}`], ["Margin %", `${d.gross_margin_pct}%`],
    ["Payments gross", `€${d.payments_gross_eur}`], ["Payments", d.payments_count],
  ];

  return (
    <div className="p-6 lg:p-10" data-testid="admin-dashboard">
      <p className="overline">Admin Studio</p>
      <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">Business dashboard</h1>
      <div className="mt-9 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {cards.map(([label, val]) => (
          <div key={label} className="rounded-xl border border-[#212230] bg-[#0b0b11] p-4" data-testid={`metric-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <p className="mono text-[9.5px] uppercase tracking-[0.14em] text-[#8288a0]">{label}</p>
            <p className="mt-2 font-display text-xl font-semibold">{val}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 grid gap-8 xl:grid-cols-2">
        <div>
          <p className="overline">MRR by plan</p>
          <div className="mt-4 space-y-2">
            {d.by_plan.map((p) => (
              <div key={p.code} className="flex items-center justify-between rounded-lg border border-[#1a1b26] bg-[#0e0e15] p-3.5">
                <p className="text-[12.5px]">{p.plan} <span className="mono text-[10px] text-[#8288a0]">· {p.users} users</span></p>
                <p className="mono text-[12.5px] text-[#00ff9d]">€{p.mrr}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="overline">Profitability by model</p>
          <div className="mt-4 space-y-2">
            {prof.map((r) => (
              <div key={r.key} className="rounded-lg border border-[#1a1b26] bg-[#0e0e15] p-3.5" data-testid={`profit-${r.key}`}>
                <div className="flex justify-between">
                  <p className="mono text-[11.5px]">{r.key}</p>
                  <p className="mono text-[11.5px] text-[#00ff9d]">€{r.margin_eur} · {r.margin_pct}%</p>
                </div>
                <p className="mono mt-1 text-[10px] text-[#8288a0]">
                  {r.generations} gens · {r.credits} cr · API €{r.api_cost_eur} · rev €{r.revenue_eur}
                </p>
              </div>
            ))}
            {prof.length === 0 && <p className="text-[13px] text-[#9194a6]">No generations yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get(`/admin/users${q ? `?q=${q}` : ""}`).then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, [q]); // eslint-disable-line

  const patch = async (uid, body) => {
    try { await api.patch(`/admin/users/${uid}`, body); load(); toast.success("Updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-users">
      <p className="overline">Users</p>
      <input className={`${inp} mt-4 max-w-[320px]`} placeholder="Search email or name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-user-search" />
      <div className="mt-6 overflow-x-auto rounded-xl border border-[#212230]">
        <table className="w-full min-w-[900px] text-left">
          <thead className="bg-[#0e0e15]">
            <tr className="mono text-[10px] uppercase tracking-[0.14em] text-[#8288a0]">
              {["Email", "Plan", "Credits", "Gens", "Projects", "Role", "Actions"].map((h) => <th key={h} className="p-3.5">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.user_id} className="border-t border-[#16181f] text-[12.5px]" data-testid={`admin-user-${u.user_id}`}>
                <td className="p-3.5">{u.email}{u.suspended && <span className="mono ml-2 text-[9px] text-[#ff4b4b]">SUSPENDED</span>}</td>
                <td className="p-3.5">
                  <select className={`${inp} w-32`} value={u.plan} onChange={(e) => patch(u.user_id, { plan: e.target.value })} data-testid={`admin-plan-${u.user_id}`}>
                    {["free", "starter", "creator", "pro", "director", "studio", "production_house", "enterprise"].map((p) => <option key={p} value={p} children={String(p)} />)}
                  </select>
                </td>
                <td className="mono p-3.5">{Number(u.credits).toLocaleString()}</td>
                <td className="mono p-3.5">{u.generations}</td>
                <td className="mono p-3.5">{u.projects}</td>
                <td className="mono p-3.5">{u.role}</td>
                <td className="p-3.5">
                  <div className="flex gap-1.5">
                    <button onClick={() => patch(u.user_id, { credits_delta: 1000 })} data-testid={`admin-grant-${u.user_id}`} className="btn-ghost rounded-full px-2.5 py-1 text-[10px] font-semibold">
                      <Zap className="mr-1 inline h-2.5 w-2.5" />+1k
                    </button>
                    <button onClick={() => patch(u.user_id, { suspended: !u.suspended })} data-testid={`admin-suspend-${u.user_id}`} className="btn-ghost rounded-full px-2.5 py-1 text-[10px] font-semibold">
                      {u.suspended ? "Unban" : "Ban"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EngineHealth() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/engines/health").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, []);
  const reset = async (id) => {
    try { await api.post(`/admin/engines/${id}/reset`); load(); toast.success("Engine un-parked"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!data) return null;
  const s = data.summary;
  const video = data.engines.filter((e) => e.kind === "video");
  return (
    <div className="mb-10 rounded-2xl border border-[#212230] bg-[#0b0b11] p-6" data-testid="engine-health">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">Engine health · 24h</p>
          <h2 className="mt-2 font-display text-xl font-semibold">
            A video request must come back as a video
          </h2>
        </div>
        <div className="flex flex-wrap gap-6">
          {[["Video engines live", s.video_engines_live, "#00ff9d"],
            ["Parked", s.video_engines_parked, s.video_engines_parked ? "#ffc76b" : "#8288a0"],
            ["Video jobs 24h", s.video_requests_24h, "#7de8ff"],
            ["Wrong output kind", s.wrong_kind_24h, s.wrong_kind_24h ? "#ff4b4b" : "#00ff9d"]]
            .map(([label, value, color]) => (
            <div key={label}>
              <p className="mono text-[9.5px] uppercase tracking-[0.14em] text-[#8288a0]">{label}</p>
              <p className="mono mt-1 text-xl font-semibold" style={{ color }}>{value}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-6 space-y-2">
        {video.map((e) => (
          <div key={e.id} data-testid={`engine-row-${e.id}`}
               className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-[#1a1b26] bg-[#0e0e15] px-4 py-3">
            <span className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: e.parked ? "#ff4b4b" : "#00ff9d" }} />
            <p className="min-w-[200px] text-[12.5px] font-medium">{e.label}</p>
            <p className="mono text-[10px] uppercase text-[#8288a0]">
              {e.completed_24h} ok · {e.failed_24h} ko
              {e.success_rate !== null ? ` · ${e.success_rate}%` : " · no traffic"}
            </p>
            {e.wrong_kind_24h > 0 && (
              <p className="mono text-[10px] uppercase text-[#ff4b4b]">
                {e.wrong_kind_24h} non-video output
              </p>
            )}
            {e.parked && (
              <>
                <p className="mono text-[10px] text-[#ffc76b]">parked until {e.unhealthy_until?.slice(11, 19)} UTC</p>
                <button onClick={() => reset(e.id)} data-testid={`engine-reset-${e.id}`}
                        className="btn-ghost rounded-full px-3 py-1 text-[10.5px] font-semibold">
                  Un-park
                </button>
              </>
            )}
            {e.last_error && (
              <p className="mono w-full truncate text-[10px] text-[#8288a0]">{e.last_error}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminModels() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/models").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const patch = async (id, body) => {
    try { await api.patch(`/admin/models/${id}`, body); load(); toast.success("Model updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="p-6 lg:p-10" data-testid="admin-models">
      <EngineHealth />
      <p className="overline">Model registry</p>
      <h1 className="mt-3 font-display text-2xl font-semibold">No model price is hard-coded</h1>
      <div className="mt-8 space-y-3">
        {list.map((m) => (
          <div key={m.id} className="grid gap-3 rounded-xl border border-[#212230] bg-[#0b0b11] p-5 lg:grid-cols-[1.4fr_repeat(5,1fr)]" data-testid={`admin-model-${m.id}`}>
            <div>
              <p className="text-[13px] font-medium">{m.label}</p>
              <p className="mono mt-1 text-[10px] uppercase text-[#8288a0]">{m.provider} · {m.model} · {m.kind}</p>
              {m.kind === "video" && (
                <div className="mt-2 space-y-1.5">
                  <input className={inp} defaultValue={m.endpoint_t2v || ""} placeholder="text-to-video endpoint"
                    onBlur={(e) => patch(m.id, { endpoint_t2v: e.target.value })}
                    data-testid={`model-t2v-${m.id}`} />
                  <input className={inp} defaultValue={m.endpoint_i2v || ""} placeholder="image-to-video endpoint"
                    onBlur={(e) => patch(m.id, { endpoint_i2v: e.target.value })}
                    data-testid={`model-i2v-${m.id}`} />
                </div>
              )}
              {m.kind === "audio" && m.provider === "fal" && (
                <input className={`${inp} mt-2`} defaultValue={m.endpoint_audio || ""} placeholder="audio endpoint"
                  onBlur={(e) => patch(m.id, { endpoint_audio: e.target.value })}
                  data-testid={`model-audio-${m.id}`} />
              )}
            </div>
            <div><p className="mono text-[9.5px] uppercase text-[#8288a0]">API €/s</p>
              <input className={inp} defaultValue={m.api_cost_per_second} onBlur={(e) => patch(m.id, { api_cost_per_second: Number(e.target.value) })} data-testid={`model-apicost-${m.id}`} /></div>
            <div><p className="mono text-[9.5px] uppercase text-[#8288a0]">Credits/s</p>
              <input className={inp} defaultValue={m.credits_per_second} onBlur={(e) => patch(m.id, { credits_per_second: Number(e.target.value) })} data-testid={`model-credits-${m.id}`} /></div>
            <div><p className="mono text-[9.5px] uppercase text-[#8288a0]">Max dur.</p>
              <input className={inp} defaultValue={m.max_duration} onBlur={(e) => patch(m.id, { max_duration: Number(e.target.value) })} /></div>
            <div><p className="mono text-[9.5px] uppercase text-[#8288a0]">Priority</p>
              <input className={inp} defaultValue={m.priority} onBlur={(e) => patch(m.id, { priority: Number(e.target.value) })} /></div>
            <div><p className="mono text-[9.5px] uppercase text-[#8288a0]">Status</p>
              <select className={inp} value={m.status} onChange={(e) => patch(m.id, { status: e.target.value })} data-testid={`model-status-${m.id}`}>
                {["active", "beta", "disabled", "needs_api_key"].map((s) => <option key={s} value={s} children={String(s)} />)}
              </select></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const load = () => {
    api.get("/admin/plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/admin/packs").then(({ data }) => setPacks(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="p-6 lg:p-10" data-testid="admin-plans">
      <p className="overline">Plans</p>
      <div className="mt-6 space-y-3">
        {plans.map((p) => (
          <div key={p.code} className="grid gap-3 rounded-xl border border-[#212230] bg-[#0b0b11] p-5 sm:grid-cols-4" data-testid={`admin-plan-${p.code}`}>
            <p className="text-[13px] font-medium">{p.name}</p>
            <div><p className="mono text-[9.5px] uppercase text-[#8288a0]">€/month</p>
              <input className={inp} defaultValue={p.price_monthly} onBlur={async (e) => { await api.patch(`/admin/plans/${p.code}`, { price_monthly: Number(e.target.value) }); load(); }} data-testid={`plan-price-${p.code}`} /></div>
            <div><p className="mono text-[9.5px] uppercase text-[#8288a0]">€/year</p>
              <input className={inp} defaultValue={p.price_yearly} onBlur={async (e) => { await api.patch(`/admin/plans/${p.code}`, { price_yearly: Number(e.target.value) }); load(); }} /></div>
            <div><p className="mono text-[9.5px] uppercase text-[#8288a0]">Credits/month</p>
              <input className={inp} defaultValue={p.credits_monthly} onBlur={async (e) => { await api.patch(`/admin/plans/${p.code}`, { credits_monthly: Number(e.target.value) }); load(); }} data-testid={`plan-credits-${p.code}`} /></div>
          </div>
        ))}
      </div>
      <p className="overline mt-12">Credit packs</p>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {packs.map((p) => (
          <div key={p.code} className="rounded-xl border border-[#212230] bg-[#0b0b11] p-5" data-testid={`admin-pack-${p.code}`}>
            <p className="text-[13px] font-medium">{p.name}</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div><p className="mono text-[9.5px] uppercase text-[#8288a0]">Credits</p>
                <input className={inp} defaultValue={p.credits} onBlur={async (e) => { await api.patch(`/admin/packs/${p.code}`, { credits: Number(e.target.value) }); load(); }} /></div>
              <div><p className="mono text-[9.5px] uppercase text-[#8288a0]">€ price</p>
                <input className={inp} defaultValue={p.price} onBlur={async (e) => { await api.patch(`/admin/packs/${p.code}`, { price: Number(e.target.value) }); load(); }} /></div>
            </div>
            <p className="mono mt-3 text-[9.5px] text-[#8288a0]">Stripe price change requires re-running setup_stripe.py</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminTemplates() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/templates?limit=500").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="p-6 lg:p-10" data-testid="admin-templates">
      <p className="overline">Templates · {list.length}</p>
      <div className="mt-6 overflow-x-auto rounded-xl border border-[#212230]">
        <table className="w-full min-w-[800px] text-left">
          <thead className="bg-[#0e0e15]"><tr className="mono text-[10px] uppercase tracking-[0.14em] text-[#8288a0]">
            {["Title", "Category", "Credits", "Premium", "Visibility"].map((h) => <th key={h} className="p-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {list.slice(0, 200).map((t) => (
              <tr key={t.id} className="border-t border-[#16181f] text-[12px]" data-testid={`admin-tpl-${t.id}`}>
                <td className="p-3">{t.title}</td>
                <td className="mono p-3 text-[10.5px]">{t.category}</td>
                <td className="p-3"><input className={`${inp} w-20`} defaultValue={t.credit_hint} onBlur={async (e) => { await api.patch(`/admin/templates/${t.id}`, { credit_hint: Number(e.target.value) }); }} /></td>
                <td className="p-3"><button onClick={async () => { await api.patch(`/admin/templates/${t.id}`, { premium: !t.premium }); load(); }} className="mono text-[10.5px] text-[#ff4b2b]">{t.premium ? "PREMIUM" : "FREE"}</button></td>
                <td className="p-3"><button onClick={async () => { await api.patch(`/admin/templates/${t.id}`, { visibility: t.visibility === "public" ? "hidden" : "public" }); load(); }} className="mono text-[10.5px]">{t.visibility}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminPayments() {
  const [list, setList] = useState([]);
  const [hooks, setHooks] = useState([]);
  const load = () => {
    api.get("/admin/payments").then(({ data }) => setList(data)).catch(() => {});
    api.get("/admin/webhooks").then(({ data }) => setHooks(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="p-6 lg:p-10" data-testid="admin-payments">
      <p className="overline">Transactions</p>
      <div className="mt-6 space-y-2">
        {list.map((t) => (
          <div key={t.session_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#1a1b26] bg-[#0e0e15] p-4" data-testid={`admin-tx-${t.session_id}`}>
            <p className="mono text-[11px] text-[#9194a6]">{t.lookup_key} · €{Number(t.amount).toFixed(2)} · {t.payment_status}</p>
            {t.payment_status === "paid" && (
              <button onClick={async () => { try { await api.post(`/admin/payments/${t.session_id}/refund`); load(); toast.success("Refunded"); } catch (e) { toast.error(apiErr(e)); } }} data-testid={`refund-${t.session_id}`} className="btn-ghost rounded-full px-3 py-1.5 text-[10.5px] font-semibold">
                Refund
              </button>
            )}
          </div>
        ))}
        {list.length === 0 && <p className="text-[13px] text-[#9194a6]">No transactions yet.</p>}
      </div>
      <p className="overline mt-12">Webhook events</p>
      <div className="mt-4 space-y-1.5">
        {hooks.map((h) => <p key={h.event_id} className="mono text-[10.5px] text-[#8288a0]">{h.type} · {h.event_id}</p>)}
        {hooks.length === 0 && <p className="text-[13px] text-[#9194a6]">—</p>}
      </div>
    </div>
  );
}

export function AdminPricing() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/admin/pricing").then(({ data }) => setP(data)).catch(() => {}); }, []);
  if (!p) return <div className="p-10 text-[13px] text-[#9194a6]">…</div>;

  const save = async (patch) => {
    try { const { data } = await api.put("/admin/pricing", { ...p, ...patch }); setP(data); toast.success("Saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-pricing">
      <p className="overline">Credit pricing engine</p>
      <h1 className="mt-3 font-display text-2xl font-semibold">Multipliers & margin</h1>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {["quality_multipliers", "resolution_multipliers"].map((group) => (
          <div key={group} className="rounded-xl border border-[#212230] bg-[#0b0b11] p-5">
            <p className="mono text-[10px] uppercase tracking-[0.14em] text-[#ff4b2b]">{group.replace("_", " ")}</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {Object.entries(p[group]).map(([k, v]) => (
                <div key={k}>
                  <p className="mono text-[9.5px] uppercase text-[#8288a0]">{k}</p>
                  <input className={inp} defaultValue={v} data-testid={`pricing-${group}-${k}`}
                    onBlur={(e) => save({ [group]: { ...p[group], [k]: Number(e.target.value) } })} />
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="rounded-xl border border-[#212230] bg-[#0b0b11] p-5">
          <p className="mono text-[10px] uppercase tracking-[0.14em] text-[#ff4b2b]">Revenue per credit (€)</p>
          <input className={`${inp} mt-4`} defaultValue={p.revenue_per_credit_eur} data-testid="pricing-revenue-per-credit"
            onBlur={(e) => save({ revenue_per_credit_eur: Number(e.target.value) })} />
          <label className="mt-5 flex items-center gap-3 text-[12.5px]">
            <input type="checkbox" className="h-4 w-4 accent-[#ff4b2b]" checked={p.refund_failed_generations} onChange={(e) => save({ refund_failed_generations: e.target.checked })} data-testid="pricing-refund-toggle" />
            Refund failed generations
          </label>
        </div>
      </div>
    </div>
  );
}

export function AdminSeo() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/seo").then(({ data }) => setList(data)).catch(() => {}); }, []);
  return (
    <div className="p-6 lg:p-10" data-testid="admin-seo">
      <p className="overline">SEO pages · {list.length}</p>
      <div className="mt-6 overflow-x-auto rounded-xl border border-[#212230]">
        <table className="w-full min-w-[800px] text-left">
          <thead className="bg-[#0e0e15]"><tr className="mono text-[10px] uppercase tracking-[0.14em] text-[#8288a0]">
            {["Locale", "Slug", "Group", "H1", "Title"].map((h) => <th key={h} className="p-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-t border-[#16181f] text-[12px]">
                <td className="mono p-3 uppercase">{s.locale}</td>
                <td className="mono p-3 text-[10.5px]">/{s.slug}</td>
                <td className="mono p-3 text-[10.5px]">{s.group}</td>
                <td className="p-3">{s.h1}</td>
                <td className="p-3 text-[#9194a6]">{s.title}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminAlerts() {
  const [d, setD] = useState(null);
  const [cfg, setCfg] = useState(null);

  const load = () => api.get("/admin/alerts").then(({ data }) => {
    setD(data);
    setCfg(data.config);
  }).catch(() => {});

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/admin/alerts/config", {
        min_margin_pct: Number(cfg.min_margin_pct),
        quota_warning_pct: Number(cfg.quota_warning_pct),
        window_days: Number(cfg.window_days),
      });
      toast.success("Thresholds saved");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!d || !cfg) return <div className="p-6 lg:p-10"><p className="mono text-[11px] text-[#9194a6]">…</p></div>;

  return (
    <div className="p-6 lg:p-10" data-testid="admin-alerts">
      <p className="overline">Profitability</p>
      <h1 className="mt-3 flex items-center gap-3 font-display text-3xl font-semibold sm:text-4xl">
        <AlertTriangle className={`h-7 w-7 ${d.count ? "text-[#ff4b2b]" : "text-[#00ff9d]"}`} />
        {d.count} alert{d.count === 1 ? "" : "s"}
      </h1>
      <p className="mt-4 max-w-[640px] text-sm text-[#9194a6]">
        Users whose real gross margin fell under your floor, or who are about to burn through
        their monthly quota. Checked over the last {cfg.window_days} days.
      </p>

      <div className="mt-8 grid max-w-[760px] gap-3 sm:grid-cols-4">
        {[["min_margin_pct", "Min margin %"], ["quota_warning_pct", "Quota warning %"],
          ["window_days", "Window (days)"]].map(([k, label]) => (
          <label key={k} className="block">
            <span className="mono text-[9.5px] uppercase tracking-[0.14em] text-[#8288a0]">{label}</span>
            <input type="number" className={`${inp} mt-2`} value={cfg[k]}
              data-testid={`alert-cfg-${k}`}
              onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} />
          </label>
        ))}
        <button onClick={save} data-testid="alert-cfg-save"
          className="btn-primary mt-6 rounded-lg py-2.5 text-[12.5px] font-semibold">Save</button>
      </div>

      <div className="mt-10 divide-y divide-[#16181f] rounded-xl border border-[#212230] bg-[#0b0b11]">
        {d.alerts.map((a) => (
          <div key={a.user_id} className="flex flex-wrap items-center justify-between gap-4 p-5"
            data-testid={`alert-${a.user_id}`}>
            <div>
              <p className="text-[13px] font-medium">{a.email}</p>
              <p className="mono mt-1 text-[10.5px] text-[#8288a0]">
                {a.plan_name} · {a.generations} gens · {a.credits_used.toLocaleString()} cr
                {a.quota ? ` / ${a.quota.toLocaleString()} (${a.usage_pct}%)` : ""}
              </p>
              <p className="mt-1.5 text-[11.5px] text-[#ffc76b]">{a.reasons.join(" · ")}</p>
            </div>
            <div className="text-right">
              <p className={`mono text-[12.5px] ${a.margin_pct < cfg.min_margin_pct ? "text-[#ff8a8a]" : "text-[#00ff9d]"}`}>
                {a.margin_pct}% · €{a.margin_eur}
              </p>
              <p className="mono mt-1 text-[10px] text-[#8288a0]">
                API €{a.api_cost_eur} · rev €{a.revenue_eur}
              </p>
            </div>
          </div>
        ))}
        {d.alerts.length === 0 && (
          <p className="p-6 text-[13px] text-[#9194a6]" data-testid="alerts-empty">
            No alert. Every account is above your margin floor.
          </p>
        )}
      </div>
    </div>
  );
}
